import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Collapse } from "@mui/material";
import { CheckedUserListForCorp, UserAssignmentPlanSelection } from "Interface/interface";
import ThemeContext from "components/app/Context";
import CheckBox from "components/common/Checkbox";
import Scroller from "components/common/Scroller/InfiniteScroller";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { isEmpty } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { PlanAssigmentLimit, SME_PAGE_SIZE } from "utils/CommonConstants";
import { getUserNameWithConditionalCheck } from "utils/SharedUtils";
import { fetchMyListDetails, fetchMyLists } from "../../SME/sme.slice";
import BubbleToggle from "../BubbleToggle";
import {
  fetchAssigneeDetailsAsync,
  setIsErrorWhenLimitExceed,
  setPayloadData,
  setUserAssignmentTabValue
} from "../PlanAssignment.slice";
import "./UserAssignment.scss";

interface MyLists {
  records: UserAssignmentPlanSelection[];
  count: number;
}

interface UserAssignmentProps {
  height: number | undefined;
}

const UserAssignment = () => {
  const dispatch = useAppDispatch();
  const translate = useTranslate();

  const payloadData: any = useAppSelector((state) => state.PlanAssignment?.payloadData);
  const userAssignmentTabValue: any = useAppSelector((state) => state.PlanAssignment?.userAssignmentTabValue);
  const myLists: any = useAppSelector((state) => state.sme.myLists);
  const myListsInfo: any = useAppSelector((state) => state.sme?.myListDetails?.mappings);
  const userDetails: any = useAppSelector((state) => state.PlanAssignment.userList);
  const userCheckedList = payloadData?.users?.hltId || [];

  const [page, setPage] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [tabValue, setTabValue] = useState<number>(userAssignmentTabValue);
  const [checkedList, setCheckedList] = useState<CheckedUserListForCorp[] | []>(userCheckedList);
  const [myListsDetails, setMyListsDetails] = useState<any>([]);
  const { isMobileScreen } = useContext(ThemeContext);
  const [expanded, setExpanded] = useState(false);

  const { records, count }: MyLists = myLists;

  const handleAreaChange = (newValue: number) => {
    setExpanded(false);
    setTabValue(newValue);
    dispatch(setUserAssignmentTabValue(newValue));
    setCheckedList([]);
    dispatch(fetchMyListDetails({ id: records[newValue]?.id, view: "inncodes", size: SME_PAGE_SIZE }));
  };

  useEffect(() => {
    if (isEmpty(records)) {
      const props = {
        searchText: "",
        page: 0,
        size: SME_PAGE_SIZE
      };

      dispatch(fetchMyLists(props)).then((res: any) => {
        if (res?.payload?.innCodeGroups[0]?.id) {
          dispatch(
            fetchMyListDetails({
              id: res?.payload?.innCodeGroups[0]?.id,
              view: "inncodes",
              size: SME_PAGE_SIZE
            })
          );
        }
      });
    }
  }, [records]);

  useEffect(() => {
    const element = document.querySelector(".component-render") as HTMLElement | null;
    if (element?.offsetHeight) {
      setContainerHeight(element.offsetHeight);
    }
  }, []);

  useEffect(() => {
    if (myListsInfo?.length) {
      setMyListsDetails(myListsInfo?.filter((item: any) => item?.hltIds?.[0]));
      const hltId = myListsInfo?.filter((item: any) => item?.hltIds?.[0]).map((item: any) => item?.hltIds?.[0]);
      const data = {
        hltIds: hltId,
        size: hltId?.length,
        includecorpusers: false
      };
      if (hltId?.length) dispatch(fetchAssigneeDetailsAsync(data));
    }
  }, [myListsInfo]);

  const fetchMore = () => {
    const props = {
      page: page + 1,
      size: SME_PAGE_SIZE
    };
    dispatch(fetchMyLists(props));
    setPage((pageNo) => pageNo + 1);
  };

  // Removing top margin, stepper and bottom count space from main container
  const remainingHeight = containerHeight - (32 + 60 + 54 + 57);
  const convertHeight = `${remainingHeight}px`;

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    dispatch(setUserAssignmentTabValue(newValue));
    setCheckedList([]);
    dispatch(fetchMyListDetails({ id: records[newValue]?.id, view: "inncodes", size: SME_PAGE_SIZE }));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, value: any, isSelectAll: boolean) => {
    if (isSelectAll && event.target.checked) {
      setCheckedList(myListsDetails);
      return;
    } else if (isSelectAll && !event.target.checked) {
      setCheckedList([]);
      return;
    }
    if (event.target.checked) {
      if (checkedList.findIndex((element: any) => element?.ids?.[0] === value?.ids?.[0]) < 0) {
        setCheckedList((prevState) => [...prevState, value]);
      }
    } else {
      setCheckedList((prevState) => prevState.filter((item: any) => item?.ids[0] !== value?.ids[0]));
    }
  };

  useEffect(() => {
    const mappings = checkedList.map((data) => data.ids?.[0]);
    dispatch(setPayloadData({ users: { mappings, hltId: checkedList } }));
    const plans = payloadData?.plans?.length || 1;
    const isError = checkedList.length * plans > PlanAssigmentLimit;
    dispatch(setIsErrorWhenLimitExceed(isError));
  }, [checkedList]);

  const listNames = () => {
    return (
      <>
        <div className="list-name-text">
          <Typography color="#4D4745" variant="body2">
            Lists
          </Typography>
        </div>
        <Scroller
          dataLength={records?.length}
          loadMore={fetchMore}
          totalList={count}
          label="inncode list"
          scrollableTarget="user-assignment-left"
        >
          {records?.map((data, index) => {
            return (
              <div
                key={`list-name-${index}`}
                className={`list-names ${index === tabValue ? "selected-listName" : ""}`}
                onClick={() => handleTabChange(index)}
              >
                <Typography className="ellipse-one" variant="body2" showBold color="#3E3A39">
                  {data.name}
                </Typography>
              </div>
            );
          })}
        </Scroller>
      </>
    );
  };

  const mapOwnerInfo = (ownerCode: string) => {
    return userDetails?.filter((item: any) => item?.hltId === ownerCode)[0];
  };

  const inncodeLists = () => {
    return (
      <>
        <div className="inncode-list-wrapper">
          <div className="select-allInncodes">
            <CheckBox
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckboxChange(e, {}, true)}
              sx={{ padding: "0", margin: "0" }}
              checked={myListsDetails?.length === checkedList?.length && myListsDetails?.length}
            />
            <Typography color="#3E3A39" variant="body2">
              Inncodes
            </Typography>
          </div>
          <div className="inncode-text">
            <Typography color="#3E3A39" variant="body2">
              User Name
            </Typography>
          </div>
        </div>

        {!isEmpty(myListsDetails) ? (
          myListsDetails?.map((item: any, i: number) => {
            const userdetails = mapOwnerInfo(item?.hltIds?.[0]);
            return (
              <div className="inncode-list-container" key={`${i}-inncode-list`}>
                <div className="inncode-name">
                  <CheckBox
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckboxChange(e, item, false)}
                    sx={{ padding: "0", margin: "0" }}
                    checked={checkedList.findIndex((el: any) => el?.ids[0] === item?.ids[0]) >= 0}
                  />
                  <Typography color="#3E3A39" variant="body2">
                    {item?.innCode}
                  </Typography>
                </div>
                <div className="inncode-user-name">
                  <Typography color="#3E3A39" variant="body2">
                    {getUserNameWithConditionalCheck(
                      userdetails?.displayName,
                      userdetails?.firstName,
                      userdetails?.lastName
                    )}
                  </Typography>
                </div>
              </div>
            );
          })
        ) : (
          <div>No Record found!</div>
        )}
        <div></div>
      </>
    );
  };

  return (
    <div className="user-assignment-component">
      <div className="user-assignment-heading">
        <Typography variant="h5" color="#312F2E" className="heading">
          Select one or more Plan Members
        </Typography>
        <BubbleToggle />
      </div>
      <div className="selection-container" onClick={() => setExpanded((prevState) => !prevState)}>
        <Typography variant="body2" showBold sx={{ color: tokens.Text.General.Color }}>
          {records[tabValue]?.name}
        </Typography>
        {expanded ? (
          <ExpandLessIcon sx={{ color: tokens.Text.General.Color }} />
        ) : (
          <ExpandMoreIcon sx={{ color: tokens.Text.General.Color }} />
        )}
      </div>
      {isMobileScreen && (
        <Collapse in={expanded}>
          {records.map((list: any, index: number) => (
            <Typography
              variant="body2"
              className={`ellipse-one ${records[tabValue]?.name === list.name ? "selected-list" : ""}`}
              sx={{ padding: "16px", color: tokens.Text.General.PrimaryHeadline, cursor: "pointer" }}
              onClick={() => handleAreaChange(index)}
              key={`${index}-list-name`}
            >
              {list?.name}
            </Typography>
          ))}
        </Collapse>
      )}
      <div className="user-assignment-container">
        {!isMobileScreen && (
          <div style={{ height: convertHeight }} className="user-assignment-left" id="user-assignment-left">
            {listNames()}
          </div>
        )}
        <div className="user-assignment-right">{inncodeLists()}</div>
      </div>
    </div>
  );
};

export default UserAssignment;
